import { coalesce } from '~/helpers/coalesce';
import { i18n, t as defaultT } from './i18n';

type Translator = (key: string) => string;

/**
 * Picks the best matching language code from a list of available codes.
 *
 * Falls back from exact match (e.g. "fr-ca") to prefix match (e.g. "fr-fr"
 * for desired "fr-ca") to the alphabetically first language by its translated
 * label. When no desired language is provided, the current i18n language is
 * used. A custom translator can be supplied for the label-based sort, which
 * lets React callers pass the `t` from `useTranslation()`.
 */
const pickLanguageCode = (
  availableLanguageCodes: string[] | null | undefined,
  desiredLanguage?: string | null,
  translator?: Translator,
): string | null => {
  const codes = coalesce(availableLanguageCodes, [] as string[]);
  if (codes.length === 0) return null;

  const requested = coalesce(desiredLanguage, i18n.language);
  const desired = coalesce(requested, '').toLowerCase();

  if (desired && codes.includes(desired)) return desired;

  const prefix = desired.split('-')[0];
  if (prefix) {
    const match = codes.find((code) => code.split('-')[0] === prefix);
    if (match) return match;
  }

  const translate = coalesce(translator, defaultT as Translator);
  return [...codes].sort((a, b) => translate(a).localeCompare(translate(b)))[0];
};

export { pickLanguageCode };
